/**
 * MentionDetailPanel — right-rail drill-down for Top Mentions (bd deepcontext-d68.9,
 * restyled per the locked mockup — bd deepcontext-4dyp).
 *
 * Pure presentational. The parent (TopMentionsPage) owns selection state and
 * passes the entity + sentiment-label map + the active theme's sentiment
 * colours. Follows the "Clusters rail anatomy" already locked for
 * TopicDetailPanel (bd deepcontext-0ihe): name + uppercase kicker + close,
 * canvas-token grid cells, then a labelled list.
 *
 * Sentiment colours are the shared theme status triad (chartTheme.ts) —
 * safe-green positive, muted-gray neutral, risk-red negative — passed down
 * from TopMentionsPage's `useChartPalette()` so the ring, legend, rail bars,
 * and article dots all draw from one source instead of a fixed hex set. The
 * accent purple never appears here: it stays reserved for interactive state.
 */

import React from 'react';
import {Card, CardContent} from '../../../components/ui/card';
import {Button} from '../../../components/ui/button';
import type {TopMentionEntity} from '../../../api/clusters';

export interface MentionSentimentColors {
    positive: string;
    neutral: string;
    negative: string;
}

/** Maps the wire sentiment bucket (1=negative/2=neutral/3=positive) to a colour. */
export function sentimentColourFor(
    bucket: number | null | undefined,
    colors: MentionSentimentColors,
): string {
    if (bucket === 1) return colors.negative;
    if (bucket === 2) return colors.neutral;
    if (bucket === 3) return colors.positive;
    return colors.neutral;
}

function fmtBig(n: number): string {
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
}

function hostFromUrl(url: string): string {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
}

interface MentionDetailPanelProps {
    entity: TopMentionEntity | null;
    sentimentLabels: Record<number, string>;
    colors: MentionSentimentColors;
    onClose: () => void;
}

const MentionDetailPanel: React.FC<MentionDetailPanelProps> = ({
    entity,
    sentimentLabels,
    colors,
    onClose,
}) => {
    if (!entity) {
        return (
            <Card>
                <CardContent className="px-4 py-6">
                    <div data-testid="mention-detail-empty">
                        <h4 className="text-sm font-semibold mb-1">Click any bubble</h4>
                        <p className="text-xs text-ink-muted leading-relaxed">
                            See every article mentioning that entity, with publisher
                            and sentiment.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const {sentiment_breakdown: sb} = entity;
    const total = sb.negative + sb.neutral + sb.positive;
    const tracks: Array<{key: 'positive' | 'neutral' | 'negative'; label: string; value: number; color: string}> = [
        {key: 'positive', label: sentimentLabels[3] ?? 'Positive', value: sb.positive, color: colors.positive},
        {key: 'neutral', label: sentimentLabels[2] ?? 'Neutral', value: sb.neutral, color: colors.neutral},
        {key: 'negative', label: sentimentLabels[1] ?? 'Negative', value: sb.negative, color: colors.negative},
    ];

    return (
        <Card className="w-full">
            <CardContent className="px-4 py-4 space-y-3">
                <div
                    data-testid="mention-detail-panel"
                    data-entity-id={entity.id}
                    className="space-y-3"
                >
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <h3
                                data-testid="mention-detail-heading"
                                className="text-base font-semibold text-ink"
                            >
                                {entity.label}
                            </h3>
                            {/* Locked mockup: uppercase, letter-spaced kicker under
                                the name, not a plain sentence-case caption. */}
                            <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted mt-0.5">
                                Entity
                            </p>
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={onClose}
                            aria-label="Close mention detail"
                        >
                            ×
                        </Button>
                    </div>

                    {/* Canvas-token grid cells — same anatomy as TopicDetailPanel. */}
                    <dl className="grid grid-cols-2 gap-2">
                        <div className="bg-canvas rounded-xl px-3 py-2">
                            <dt className="text-xs font-bold uppercase tracking-wide text-ink-muted">
                                Mentions
                            </dt>
                            <dd
                                data-testid="mention-detail-count"
                                className="text-[15px] font-extrabold text-ink"
                            >
                                {entity.count.toLocaleString()}
                            </dd>
                        </div>
                        <div className="bg-canvas rounded-xl px-3 py-2">
                            <dt className="text-xs font-bold uppercase tracking-wide text-ink-muted">
                                Impressions
                            </dt>
                            <dd className="text-[15px] font-extrabold text-ink">
                                {fmtBig(entity.total_impressions)}
                            </dd>
                        </div>
                    </dl>

                    <div data-testid="mention-detail-sentiment">
                        <div className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-1.5">
                            Sentiment
                        </div>
                        {total === 0 ? (
                            <p className="text-xs text-ink-muted">No sentiment signal.</p>
                        ) : (
                            <div className="space-y-1">
                                {tracks.map((t) => {
                                    const pct = Math.round((t.value / total) * 100);
                                    return (
                                        <div
                                            key={t.key}
                                            data-testid={`sentiment-track-${t.key}`}
                                            className="flex items-center gap-2 text-sm font-semibold text-ink-muted"
                                        >
                                            <span className="w-14 shrink-0 truncate">{t.label}</span>
                                            <span className="flex-1 h-[7px] rounded-full bg-canvas overflow-hidden relative">
                                                <span
                                                    data-testid="sentiment-track-fill"
                                                    className="absolute inset-y-0 left-0 rounded-full"
                                                    style={{width: `${pct}%`, background: t.color}}
                                                />
                                            </span>
                                            <span className="w-9 text-right font-extrabold text-ink shrink-0">
                                                {pct}%
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-1.5">
                            Top articles
                        </div>
                        {entity.sample_urls.length === 0 ? (
                            <p className="text-xs text-ink-muted">
                                No sample articles available.
                            </p>
                        ) : (
                            <ul
                                className="space-y-1 max-h-72 overflow-y-auto"
                                data-testid="mention-detail-samples"
                            >
                                {entity.sample_urls.map((u, i) => (
                                    <li key={`${u.url_hash || i}-${i}`}>
                                        <a
                                            href={u.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-ghost-bg text-sm"
                                        >
                                            <span
                                                aria-hidden
                                                className="w-2 h-2 rounded-full shrink-0"
                                                style={{background: sentimentColourFor(u.sentiment, colors)}}
                                                title={
                                                    u.sentiment != null
                                                        ? sentimentLabels[u.sentiment] ??
                                                          String(u.sentiment)
                                                        : 'Unknown sentiment'
                                                }
                                            />
                                            <span className="font-semibold text-ink truncate">
                                                {u.url || u.site_domain || u.url_hash}
                                            </span>
                                            <span className="ml-auto text-ink-muted shrink-0">
                                                {u.site_domain ||
                                                    (u.url ? hostFromUrl(u.url) : '—')}
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default MentionDetailPanel;
